import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { StoreSettings } from '@/types';
import {
    SelectChangeEvent,
    Accordion,
    AccordionSummary,
    Typography,
    AccordionDetails,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Box,
    FormGroup,
    Checkbox,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type RfuMode = 'disable_auto_firmware' | 'one_time' | 'recurring';
type RfuUIModel = {
    mode: RfuMode;
    oneTimeAt: Dayjs | null;
    days: string[];
    hour: string;
    minute: string;
};
type RfuErrors = {
    oneTimeAt?: string | null;
    days?: string | null;
    time?: string | null;
};

const getRfuErrors = (cfg: RfuUIModel, t: (k: string) => string): RfuErrors => {
    const errors: RfuErrors = {};
    if (cfg.mode === 'one_time') {
        if (!cfg.oneTimeAt) {
            errors.oneTimeAt = t('Please select a date & time.');
        } else if (!cfg.oneTimeAt.isValid?.() || !dayjs(cfg.oneTimeAt).isValid()) {
            errors.oneTimeAt = t('Invalid date & time.');
        }
    } else if (cfg.mode === 'recurring') {
        if (!Array.isArray(cfg.days) || cfg.days.length === 0) {
            errors.days = t('Pick at least one day.');
        }
        const h = Number(cfg.hour);
        const m = Number(cfg.minute);
        const hhOk = Number.isInteger(h) && h >= 0 && h <= 23;
        const mmOk = Number.isInteger(m) && m >= 0 && m <= 59;
        if (!hhOk || !mmOk) {
            errors.time = t('Invalid HH:mm.');
        }
    }
    return errors;
};

const pad2 = (v: string): string => {
    return String(v ?? '00').padStart(2, '0');
};

const resetConfigForMode = (mode: RfuMode): RfuUIModel => {
    return { mode, oneTimeAt: null, days: [], hour: '00', minute: '00' };
};

const buildSchedule = (cfg: RfuUIModel): string => {
    if (cfg.mode === 'one_time') {
        return cfg.oneTimeAt ? `one_time|${dayjs(cfg.oneTimeAt).format('YYYY-MM-DD HH:mm:ss')}` : '';
    }
    if (cfg.mode === 'recurring') {
        const days = [...cfg.days].filter(Boolean).sort((a, b) => Number(a) - Number(b));
        return `recurring|${days.join(',')}|${pad2(cfg.hour)}:${pad2(cfg.minute)}`;
    }
    return '';
};

const parseSettingsToUI = (settings: Partial<StoreSettings>): RfuUIModel => {
    const autoOff = String(settings.auto_rfu) === '0';
    const schedule = settings.rfu_schedule ?? '';

    if (autoOff) return resetConfigForMode('disable_auto_firmware');

    const [type, rest, time] = schedule.split('|');

    if (type === 'one_time') {
        return {
            mode: 'one_time',
            oneTimeAt: rest ? dayjs(rest).local() : null,
            days: [],
            hour: '00',
            minute: '00',
        };
    }

    if (type === 'recurring') {
        const days = (rest ?? '')
            .split(',')
            .filter(Boolean)
            .sort((a, b) => Number(a) - Number(b));
        const [h = '00', m = '00'] = (time ?? '').split(':');
        return { mode: 'recurring', oneTimeAt: null, days, hour: pad2(h), minute: pad2(m) };
    }

    return resetConfigForMode('disable_auto_firmware');
};

const RemoteFirmwareUpdate = ({
    settings,
    originalSettings,
    setSettings,
    setRfuDisable,
}: {
    settings: Partial<StoreSettings>;
    originalSettings: Partial<StoreSettings>;
    setSettings: (payload: Partial<StoreSettings>) => void;
    setRfuDisable: (payload: boolean) => void;
}) => {
    const { t } = useTranslation('common');

    const [rfuConfig, setRfuConfig] = useState<RfuUIModel>(() => parseSettingsToUI(settings));
    const originalRfuConfig = useMemo(() => parseSettingsToUI(originalSettings), [originalSettings]);

    const dayLabels: Record<string, string> = useMemo(
        () => ({
            '0': t('Sun'),
            '1': t('Mon'),
            '2': t('Tue'),
            '3': t('Wed'),
            '4': t('Thu'),
            '5': t('Fri'),
            '6': t('Sat'),
        }),
        [t],
    );

    const [rfuErrors, setRfuErrors] = useState<RfuErrors>({});
    const rfuRequired = rfuConfig.mode !== 'disable_auto_firmware';

    useEffect(() => {
        if (!rfuRequired) {
            setRfuErrors({});
            setRfuDisable(false);
            return;
        }
        const errs = getRfuErrors(rfuConfig, t);
        setRfuDisable(!isEmpty(errs));
        setRfuErrors(errs);
    }, [rfuConfig, rfuRequired, t, setRfuDisable]);

    const applyToSettings = (nextCfg: RfuUIModel) => {
        setSettings({
            ...settings,
            auto_rfu: nextCfg.mode === 'disable_auto_firmware' ? '0' : '1',
            rfu_schedule: buildSchedule(nextCfg),
        });
    };

    const handleModeChange = (_event: ChangeEvent<HTMLInputElement>, value: string) => {
        const mode = value as RfuMode;

        if (mode === originalRfuConfig.mode) {
            // Patch original config
            setRfuConfig(originalRfuConfig);
            setSettings({
                ...settings,
                auto_rfu: originalSettings.auto_rfu,
                rfu_schedule: originalSettings.rfu_schedule,
            });
            return;
        }

        const nextCfg = resetConfigForMode(mode);
        setRfuConfig(nextCfg);
        applyToSettings(nextCfg);
    };

    const toggleDay = (day: string) => {
        setRfuConfig((prev) => {
            const set = new Set(prev.days);
            if (set.has(day)) set.delete(day);
            else set.add(day);

            const nextCfg: RfuUIModel = {
                ...prev,
                mode: 'recurring',
                days: Array.from(set).sort((a, b) => Number(a) - Number(b)),
            };

            applyToSettings(nextCfg);
            return nextCfg;
        });
    };

    const setHour = (event: SelectChangeEvent<string>) => {
        const newHour = event.target.value as string;
        setRfuConfig((prev) => {
            const nextCfg = { ...prev, mode: 'recurring' as RfuMode, hour: pad2(newHour) };
            applyToSettings(nextCfg);
            return nextCfg;
        });
    };

    const setMinute = (event: SelectChangeEvent<string>) => {
        const newMinute = event.target.value as string;
        setRfuConfig((prev) => {
            const nextCfg = { ...prev, mode: 'recurring' as RfuMode, minute: pad2(newMinute) };
            applyToSettings(nextCfg);
            return nextCfg;
        });
    };

    const setOneTimeAt = (value: Dayjs | null) => {
        setRfuConfig((prev) => {
            const nextCfg = { ...prev, mode: 'one_time' as RfuMode, oneTimeAt: value ?? null };
            applyToSettings(nextCfg);
            return nextCfg;
        });
    };

    return (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">{t('Remote Firmware Update')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <FormControl
                    sx={{
                        marginLeft: '1rem',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <RadioGroup value={rfuConfig.mode} onChange={handleModeChange}>
                        <FormControlLabel
                            value="disable_auto_firmware"
                            control={<Radio />}
                            label={t('Disable Auto Firmware Update')}
                        />

                        <FormControlLabel value="one_time" control={<Radio />} label={t('One Time')} />

                        {rfuConfig.mode === 'one_time' && (
                            <DateTimePicker
                                label={t('Select Date Time')}
                                format="YYYY-MM-DD HH:mm"
                                value={rfuConfig.oneTimeAt}
                                onChange={setOneTimeAt}
                                slotProps={{
                                    textField: {
                                        fullWidth: false,
                                        sx: { width: { xs: '100%', sm: 280 } },
                                        error: !!rfuErrors.oneTimeAt,
                                        helperText: rfuErrors.oneTimeAt || '',
                                    },
                                }}
                            />
                        )}

                        <FormControlLabel value="recurring" control={<Radio />} label={t('Recurring')} />

                        {rfuConfig.mode === 'recurring' && (
                            <Box>
                                <FormGroup row>
                                    {Object.entries(dayLabels).map(([dayIndex, dayLabel]) => (
                                        <FormControlLabel
                                            key={dayIndex}
                                            control={
                                                <Checkbox
                                                    checked={rfuConfig.days.includes(dayIndex)}
                                                    onChange={() => toggleDay(dayIndex)}
                                                />
                                            }
                                            label={dayLabel}
                                        />
                                    ))}
                                </FormGroup>

                                {rfuErrors.days && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                        {rfuErrors.days}
                                    </Typography>
                                )}

                                <Box display="flex" gap={2} mt={1}>
                                    <FormControl size="small" error={!!rfuErrors.time}>
                                        <InputLabel>{t('Hour')}</InputLabel>
                                        <Select value={rfuConfig.hour} onChange={setHour} label={t('Hour')}>
                                            {Array.from({ length: 24 }, (_, i) => {
                                                const val = String(i).padStart(2, '0');
                                                return (
                                                    <MenuItem key={val} value={val}>
                                                        {val}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>

                                    <FormControl size="small" error={!!rfuErrors.time}>
                                        <InputLabel>{t('Minute')}</InputLabel>
                                        <Select value={rfuConfig.minute} onChange={setMinute} label={t('Minute')}>
                                            {Array.from({ length: 60 }, (_, i) => {
                                                const val = String(i).padStart(2, '0');
                                                return (
                                                    <MenuItem key={val} value={val}>
                                                        {val}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {rfuErrors.time && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                        {rfuErrors.time}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </RadioGroup>
                </FormControl>
            </AccordionDetails>
        </Accordion>
    );
};

export default RemoteFirmwareUpdate;
